import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';
import { Category } from '../types';
interface CreatePinModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    description: string;
    imageUrl: string;
    category: string;
  }) => void;
}
const categories: Category[] = ['Interior', 'Art', 'Nature', 'Food', 'Fashion', 'Tech', 'Travel'];
export function CreatePinModal({
  isOpen,
  onClose,
  onSubmit
}: CreatePinModalProps) {
  const [imageUrl, setImageUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<Category>('Art');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate network delay
    setTimeout(() => {
      onSubmit({
        title,
        description,
        imageUrl,
        category
      });
      setIsLoading(false);
      onClose();
      // Reset form
      setTitle('');
      setDescription('');
      setImageUrl('');
    }, 800);
  };
  return <AnimatePresence data-id="element-65">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" onClick={onClose} data-id="element-66" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 m-auto w-full max-w-2xl h-fit max-h-[90vh] bg-white rounded-2xl shadow-2xl z-[70] overflow-hidden flex flex-col md:flex-row" data-id="element-67">
            <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10" data-id="element-68">
              <X className="w-5 h-5 text-gray-500" data-id="element-69" />
            </button>

            {/* Left: Image Preview/Input */}
            <div className="w-full md:w-1/2 bg-gray-50 p-8 flex flex-col items-center justify-center border-r border-gray-100 min-h-[300px]" data-id="element-70">
              {imageUrl ? <div className="relative w-full h-full rounded-lg overflow-hidden shadow-sm group" data-id="element-71">
                  <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-72" />
                  <button onClick={() => setImageUrl('')} className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white font-medium" data-id="element-73">
                    Change Image
                  </button>
                </div> : <div className="w-full h-full border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center p-6 text-center" data-id="element-74">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-4" data-id="element-75">
                    <Upload className="w-6 h-6 text-gray-500" data-id="element-76" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-77">
                    Add an image URL
                  </p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-78">
                    Paste a direct link to an image
                  </p>
                  <div className="w-full relative" data-id="element-79">
                    <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-80" />
                    <input type="url" placeholder="https://..." className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-black focus:border-transparent outline-none" value={imageUrl} onChange={e => setImageUrl(e.target.value)} data-id="element-81" />
                  </div>
                </div>}
            </div>

            {/* Right: Details Form */}
            <div className="w-full md:w-1/2 p-8 flex flex-col" data-id="element-82">
              <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-83">
                Create Pin
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4 flex-1" data-id="element-84">
                <div data-id="element-85">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-86">
                    Title
                  </label>
                  <input type="text" placeholder="Add a title" className="w-full py-2 border-b-2 border-gray-200 focus:border-black outline-none text-lg font-medium placeholder-gray-300 transition-colors" value={title} onChange={e => setTitle(e.target.value)} required data-id="element-87" />
                </div>

                <div data-id="element-88">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1" data-id="element-89">
                    Description
                  </label>
                  <textarea placeholder="Add a detailed description" className="w-full py-2 border-b-2 border-gray-200 focus:border-black outline-none text-sm resize-none placeholder-gray-300 transition-colors min-h-[80px]" value={description} onChange={e => setDescription(e.target.value)} data-id="element-90" />
                </div>

                <div data-id="element-91">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-92">
                    Category
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-93">
                    {categories.slice(0, 5).map(cat => <button key={cat} type="button" onClick={() => setCategory(cat)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${category === cat ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`} data-id="element-94">
                        {cat}
                      </button>)}
                  </div>
                </div>

                <div className="pt-4 mt-auto" data-id="element-95">
                  <button type="submit" disabled={!imageUrl || !title || isLoading} className="w-full py-3 bg-black text-white rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors flex items-center justify-center gap-2" data-id="element-96">
                    {isLoading ? 'Creating...' : 'Create Pin'}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}